/**
 * Signing in again where the user is (2026-10-14). When this device's
 * session ends (the server closed a socket with 4401, or refuses the session
 * at reconnection: engine/sync/session-check.js), a dialog asks for the password;
 * nothing is reloaded and nothing is deleted. Signed in again
 * (AuthHandlers.resumeSession), the sockets, which kept retrying, connect at
 * once (Events.SESSION_RESUMED) and send the changes made meanwhile.
 * "Later" keeps working on this device, with a banner whose "Sign in" opens
 * the dialog again. At startup with an ended session (main.js), the same
 * dialog; there the app reloads after signing in, to start its sync.
 */
import { placeBelowHeader } from "./banner-position.js";
import { t } from "../../../shared/i18n/index.js";
import { on, emit, Events } from "../../../engine/core/events.js";
import { promptForPassword } from "./auth/password-prompt.js";
import * as AuthHandlers from "../../../engine/auth/handlers.js";
import { noteConnected } from "../../../engine/sync/session-check.js";

const BANNER_ID = "session-resume-banner";
let dialogOpen = false;

function removeBanner() {
  document.getElementById(BANNER_ID)?.remove();
}

/** Not syncing until signed in again: a banner whose button opens the dialog. */
function showBanner(options) {
  if (document.getElementById(BANNER_ID)) return;
  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.className = "sibling-logout-banner";
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");
  const message = document.createElement("span");
  message.className = "sibling-logout-banner-message";
  message.textContent = t("account.resume.banner");
  const signIn = document.createElement("button");
  signIn.type = "button";
  signIn.className = "sibling-logout-banner-action";
  signIn.textContent = t("account.resume.bannerAction");
  signIn.addEventListener("click", () => { void openSessionResume(options); });
  banner.append(message, signIn);
  document.body.appendChild(banner);
  placeBelowHeader(banner);
}

/**
 * Ask for the password and sign this device in again.
 * @param {{reloadAfter?: boolean}} [options] - reloadAfter: at startup, reload
 *   once signed in (the page started without its sync)
 * @returns {Promise<boolean>} true once signed in again
 */
export async function openSessionResume(options = {}) {
  if (dialogOpen) return false;
  dialogOpen = true;
  removeBanner();
  try {
    const username = AuthHandlers.getCurrentUser?.()?.username || AuthHandlers.getCachedUsername?.() || "";
    const user = await promptForPassword({
      title: t("account.resume.title"),
      message: t("account.resume.message", { username }),
      submitLabel: t("account.resume.submit"),
      busyLabel: t("account.login.signingIn"),
      cancelLabel: t("account.resume.later"),
      action: (password) => AuthHandlers.resumeSession(password),
    });
    if (!user) {
      showBanner(options);
      return false;
    }
    noteConnected();
    if (options.reloadAfter) {
      const { reloadApp } = await import("../../../engine/storage/index.js");
      await reloadApp();
      return true;
    }
    emit(Events.SESSION_RESUMED, { user });
    return true;
  } finally {
    dialogOpen = false;
  }
}

/**
 * Open the dialog when this device's session ends, unless the page is not
 * signed in or signs itself out.
 * @param {{isSignedIn: () => boolean, isSigningOutHere: () => boolean}} cb
 */
export function initSessionResume(cb) {
  on(Events.SESSION_ENDED, () => {
    if (!cb.isSignedIn() || cb.isSigningOutHere()) return;
    void openSessionResume();
  });
  on(Events.AUTH_LOGOUT, removeBanner);
}
